"use client";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { IClientError, IEventFeedResponse } from "@finbackoffice/websocket-client";
import { IMarketOutcomeFeedResponse } from "@finbackoffice/clientbff-client";
import { useWebsocketUpdates, WebsocketContext } from "@finbackoffice/site-core";
import { BetSlipActionsContext, BetSlipSelectionContext } from "contexts";
import { EventStatus, MarketStatus, MarketOutcomeStatus } from "@finbackoffice/enums";
import { ActiveSportGameContext, MarketUpdatesContext } from "contexts";
import { FeedScopeEnum } from "utils/constants";
import { RouteNames } from "utils/constants";
import { useMarketGroups } from "./useMarketGroups";

export const LIVE_STATUSES = [
    EventStatus.Live,
    EventStatus.Suspended,
    EventStatus.Ended,
    EventStatus.Settled,
];

export const useGame = (gameId: number, feed: FeedScopeEnum) => {
    const router = useRouter();

    const { setCurrentGameStatus, currentGameStatus } = useContext(ActiveSportGameContext);
    const { socketClient } = useContext(WebsocketContext);

    const { addBetItem, removeBetItem } = useContext(BetSlipActionsContext);
    const { isOutcomeSelected } = useContext(BetSlipSelectionContext);
    const isOutcomeSelectedRef = useRef(isOutcomeSelected);
    isOutcomeSelectedRef.current = isOutcomeSelected;
    const { subscribeMarket, unsubscribeMarket } = useContext(MarketUpdatesContext);

    const [gameData, setGameData] = useState<IEventFeedResponse | null>(null);
    const gameDataRef = useRef(gameData);
    gameDataRef.current = gameData;

    const isFirstEffect = useRef(true);

    // Memoize derived values to prevent unnecessary recalculations
    const sportId = useMemo(() => gameData?.sport_id, [gameData?.sport_id]);
    const homeName = useMemo(() => gameData?.home_team?.name || "", [gameData?.home_team?.name]);
    const awayName = useMemo(() => gameData?.away_team?.name || "", [gameData?.away_team?.name]);

    const marketsMemo = useMemo(() => gameData?.markets || [], [gameData?.markets]);

    const { marketGroups, isMarketGroupFav, handleFavoriteClick } = useMarketGroups(
        gameId,
        marketsMemo,
        homeName,
        awayName,
        feed,
        sportId,
    );

    const gameUpdateHandler = useCallback(
        (response: IEventFeedResponse, error: IClientError) => {
            if (!error) {
                setGameData((state) => {
                    if (!socketClient) {
                        return state;
                    }

                    if (feed === FeedScopeEnum.results) {
                        return response; // No modification needed
                    }

                    return {
                        ...response,
                        markets: socketClient.mergeEventMarkets(
                            state?.markets || [],
                            response.markets,
                        ),
                    };
                });
            } else if (error.error === "not_found") {
                router.replace(`/${RouteNames.SPORT}/${feed}`);
            }
        },
        [feed, router, socketClient],
    );
    const payload = useMemo(() => ({ event_id: gameId }), [gameId]);
    useWebsocketUpdates("event", gameUpdateHandler, undefined, payload);

    useEffect(() => {
        return () => {
            if (gameId) {
                setGameData(null);
            }
        };
    }, [gameId]);

    useEffect(() => {
        const gameStatus = gameData?.status;
        if (!gameStatus) return;

        const nextStatus = LIVE_STATUSES.includes(gameStatus)
            ? EventStatus.Live
            : EventStatus.Prematch;

        setCurrentGameStatus(nextStatus);
    }, [gameData?.status, setCurrentGameStatus]);

    useEffect(() => {
        return () => setCurrentGameStatus(null);
    }, [setCurrentGameStatus]);

    useEffect(() => {
        const gameStatus = gameData?.status;
        if (gameStatus) {
            if (feed !== FeedScopeEnum.results && isFirstEffect.current) {
                if (currentGameStatus === EventStatus.Live) {
                    const isSettled = gameStatus === EventStatus.Settled;
                    const needRedirect =
                        isFirstEffect.current &&
                        [EventStatus.Ended, EventStatus.Prematch].includes(gameStatus);
                    if (isSettled || needRedirect) {
                        // @TODO check
                        router.replace(`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`);
                    }
                    isFirstEffect.current = false;
                }
            }
        }
    }, [currentGameStatus, feed, gameData?.status, router]);

    const addBet = useCallback(
        (
            marketName: string,
            marketStatus: MarketStatus,
            marketId: number,
            outcome: IMarketOutcomeFeedResponse,
        ) => {
            if (!isOutcomeSelectedRef.current(outcome.id) && gameDataRef.current) {
                addBetItem({
                    gameId,
                    marketId,
                    outcomeId: outcome.id,
                    team1Name: homeName,
                    team2Name: awayName,
                    marketName,
                    marketStatus,
                    eventStatus: gameDataRef.current.status,
                    outcomeName: outcome.name ?? "",
                    outcomeValue: outcome.odds,
                    outcomeStatus: MarketOutcomeStatus.Active,
                    outcomeInitValue: outcome.odds,
                    stake: "",
                    loading: false,
                    accepted: false,
                });
                subscribeMarket(gameId, marketId, outcome.id);
            } else {
                removeBetItem(outcome.id);
                unsubscribeMarket(outcome.id);
            }
        },
        [gameId, homeName, awayName, addBetItem, subscribeMarket, removeBetItem, unsubscribeMarket],
    );

    return { gameData, addBet, handleFavoriteClick, isMarketGroupFav, marketGroups };
};
